
import { useState, useCallback, useRef, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PrayerVoice } from './usePrayerVoices';
import { isIOSDevice, isSafariBrowser, createAudioUrl, revokeAudioUrl } from '@/utils/audioUtils';

export const useAzurePrayerAudio = () => {
  // State management
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // Refs for stable values between renders
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef<number>(0);
  const maxRetries = 2; // Maximum number of retries
  
  const { toast } = useToast();

  // Determine if we're on iOS or Safari
  const deviceInfo = useRef({
    isIOS: false,
    isSafari: false,
    needsSpecialHandling: false
  });

  // Set device info on mount
  useEffect(() => {
    deviceInfo.current = {
      isIOS: isIOSDevice(),
      isSafari: isSafariBrowser(),
      needsSpecialHandling: isIOSDevice() || isSafariBrowser()
    };
    
    if (deviceInfo.current.needsSpecialHandling) {
      console.log("iOS/Safari detected - using enhanced audio compatibility mode");
    }
  }, []);

  // Create audio element on first render
  useEffect(() => {
    // Create new audio element
    const audio = new Audio();
    
    // Apply iOS-specific settings
    if (deviceInfo.current.needsSpecialHandling) {
      audio.preload = "auto";  // Force preloading on iOS
      
      // Add special event for iOS progress monitoring
      audio.addEventListener('progress', () => {
        // Calculate progress based on buffered ranges if available
        if (audio.buffered && audio.buffered.length > 0) {
          const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
          const duration = audio.duration;
          if (duration > 0) {
            setLoadingProgress(Math.floor((bufferedEnd / duration) * 100));
          }
        }
      });
    }
    
    // Set up audio event listeners
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    const onCanPlayThrough = () => {
      setIsAudioReady(true);
      setLoadingProgress(100);
    };
    const onError = (e: ErrorEvent) => {
      console.error("Audio playback error:", e);
      setIsPlaying(false);
      setIsAudioReady(false);
      
      let message = "Unknown audio error";
      if (audio.error) {
        switch(audio.error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            message = "Playback aborted";
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            message = "Network error while loading audio";
            break;
          case MediaError.MEDIA_ERR_DECODE:
            message = "Audio decoding error";
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            message = "Audio format not supported";
            break;
        }
        
        // Add iOS-specific info to error message
        if (deviceInfo.current.isIOS) {
          message += " (iOS device detected)";
        }
      }
      
      setError(message);
      toast({
        title: "Audio playback error",
        description: message,
        variant: "destructive"
      });
      
      // Attempt retry for network errors on iOS
      if (retryCountRef.current < maxRetries && 
          audio.error?.code === MediaError.MEDIA_ERR_NETWORK && 
          deviceInfo.current.needsSpecialHandling) {
        retryCountRef.current += 1;
        console.log(`Retrying audio load (attempt ${retryCountRef.current}/${maxRetries})...`);
        
        // Small delay before retry
        setTimeout(() => {
          if (audio.src) {
            audio.load(); // Retry loading
          }
        }, 1000);
      }
    };
    
    // Add event listeners
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('canplaythrough', onCanPlayThrough);
    audio.addEventListener('error', onError as EventListener);
    
    // Store audio element in ref
    audioRef.current = audio;
    
    // Clean up function
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('canplaythrough', onCanPlayThrough);
      audio.removeEventListener('error', onError as EventListener);
      
      // Stop and clean up audio
      audio.pause();
      if (audio.src) {
        audio.src = '';
        audio.load();
      }
      
      // Revoke any object URL
      if (audioUrlRef.current) {
        revokeAudioUrl(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Clean up resources
  const cleanupAudio = useCallback(() => {
    // Clean up any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clean up audio element
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      if (audio.src) {
        audio.src = '';
        audio.load();
      }
    }
    
    // Clean up URL object
    if (audioUrlRef.current) {
      revokeAudioUrl(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    
    // Reset states
    setIsPlaying(false);
    setIsAudioReady(false);
    setError(null);
    setLoadingProgress(0);
    retryCountRef.current = 0;
  }, []);

  // Prepare text by removing references and shortening if needed
  const prepareTextForAudio = useCallback((text: string): string => {
    // Replace standalone references like (1 Peter 5:7) with empty string
    let prepared = text.replace(/\([^)]+:[^)]+\)/g, '');
    
    // If text is too long, truncate (Azure has a character limit but we'll be cautious)
    const maxLength = deviceInfo.current.isIOS ? 3500 : 4000; // Shorter for iOS
    if (prepared.length > maxLength) {
      prepared = prepared.substring(0, maxLength);
      console.log("Text was truncated for Azure TTS");
    }
    
    return prepared.trim();
  }, []);

  // Generate and play audio
  const generateAndPlayAudio = useCallback(async (text: string, voice: PrayerVoice) => {
    if (!audioRef.current) {
      setError("Audio player not initialized");
      toast({
        title: "Audio error",
        description: "Audio player not initialized",
        variant: "destructive"
      });
      return;
    }
    
    // Clean up previous audio and reset states
    cleanupAudio();
    setError(null);
    setIsGeneratingAudio(true);
    
    try {
      toast({
        title: "Generating audio...",
        description: `Using ${voice.name}'s voice to speak this prayer`,
      });

      // Prepare text
      const shortenedText = prepareTextForAudio(text);
      
      console.log(`Calling Azure TTS with voice: ${voice.id}`);
      
      // Create abort controller for the request
      abortControllerRef.current = new AbortController();
      
      // Call the Azure TTS Edge Function with a timeout
      // Use longer timeout for iOS devices to account for slower processing
      const timeoutDuration = deviceInfo.current.isIOS ? 40000 : 30000;
      
      const response = await Promise.race([
        supabase.functions.invoke('azure-tts', {
          body: {
            text: shortenedText,
            voiceId: voice.id,
            style: voice.style
          }
        }),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error("Request timeout")), timeoutDuration);
        })
      ]);
      
      // Reset abort controller after successful completion
      abortControllerRef.current = null;
      
      // Handle error response
      if (response.error) {
        console.error("Edge function error:", response.error);
        throw new Error(`Failed to generate audio: ${response.error.message || 'Unknown error'}`);
      }

      // No data received
      if (!response.data || !response.data.audioData) {
        console.error("No audio data received from edge function");
        throw new Error("Received empty response from server");
      }

      // Convert base64 to blob
      const base64Data = response.data.audioData;
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Create blob with explicit MIME type for better iOS compatibility
      const mimeType = response.data.format || 'audio/mpeg';
      const blob = new Blob([bytes], { type: mimeType });
      
      // Create object URL
      const url = createAudioUrl(blob, mimeType);
      audioUrlRef.current = url;
      
      const audio = audioRef.current;
      
      // Special handling for iOS
      if (deviceInfo.current.needsSpecialHandling) {
        // Set up iOS-specific load handling
        audio.preload = "auto";
      }
      
      // Set the source
      audio.src = url;
      
      // Wait for audio to load enough data before playing
      // Increased timeout for iOS devices
      const loadingTimeout = deviceInfo.current.isIOS ? 20000 : 10000;
      await new Promise<void>((resolve, reject) => {
        const loadTimeout = setTimeout(() => {
          reject(new Error("Audio loading timeout"));
        }, loadingTimeout);
        
        audio.oncanplaythrough = () => {
          clearTimeout(loadTimeout);
          resolve();
        };
        
        audio.onerror = () => {
          clearTimeout(loadTimeout);
          reject(new Error(`Audio loading failed: ${audio.error?.message || 'Unknown error'}`));
        };
        
        // Explicitly load the audio
        audio.load();
        
        // Start loading progress monitoring for iOS
        if (deviceInfo.current.needsSpecialHandling) {
          setLoadingProgress(10); // Initial progress indicator
          
          // Monitor loading progress
          const progressInterval = setInterval(() => {
            // Check if we have buffered data
            if (audio.buffered && audio.buffered.length > 0) {
              const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
              const duration = audio.duration;
              if (duration > 0) {
                setLoadingProgress(Math.min(95, Math.floor((bufferedEnd / duration) * 100)));
                
                // If we've buffered enough, we can resolve
                if (bufferedEnd / duration > 0.1) { // 10% buffered is enough to start
                  clearTimeout(loadTimeout);
                  clearInterval(progressInterval);
                  resolve();
                }
              }
            }
          }, 300);
        }
      });
      
      // Mark audio as ready
      setIsAudioReady(true);
      setLoadingProgress(100);
      
      // Play the audio with iOS-specific error handling
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (playError) {
        console.error('Error during audio play:', playError);
        
        // iOS often requires user interaction for playback
        if (deviceInfo.current.isIOS) {
          toast({
            title: "Audio ready",
            description: "Tap play to hear the prayer (iOS requires manual play)",
          });
          setIsAudioReady(true);
        } else {
          throw playError;
        }
      }
      
    } catch (error) {
      console.error('Error generating or playing audio:', error);
      
      // Set error state
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      setError(errorMessage);
      
      toast({
        title: "Failed to generate or play audio",
        description: errorMessage,
        variant: "destructive"
      });
      
      // Clean up
      cleanupAudio();
    } finally {
      setIsGeneratingAudio(false);
    }
  }, [cleanupAudio, prepareTextForAudio, toast]);

  // Toggle play/pause
  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else if (audio.src && isAudioReady) {
      // Reset retry count before attempting to play
      retryCountRef.current = 0;
      
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        const errorMsg = err instanceof Error ? err.message : "Could not play the audio";
        
        // Add iOS-specific context to the error message
        const contextualError = deviceInfo.current.isIOS 
          ? `${errorMsg} (iOS may require user interaction)` 
          : errorMsg;
          
        setError(contextualError);
        toast({
          title: "Error playing audio",
          description: contextualError,
          variant: "destructive"
        });
      });
    } else {
      toast({
        title: "No audio available",
        description: "Please generate audio first",
        variant: "destructive"
      });
    }
  }, [isPlaying, isAudioReady, toast]);

  return {
    isGeneratingAudio,
    isPlaying,
    isAudioReady,
    error,
    loadingProgress,
    isIOS: deviceInfo.current.isIOS,
    generateAndPlayAudio,
    togglePlayback,
    cleanupAudio,
    audioUrlRef // Export the audioUrlRef for download functionality
  };
};
